/**
 * Stytch MFA Recovery Codes Hooks
 *
 * Wraps the Stytch B2B headless client's `recoveryCodes` methods. Recovery codes
 * are generated when a member enrolls in TOTP and authenticate the member in place
 * of their secondary MFA factor (e.g. when they lose access to their authenticator).
 */
import { useCallback } from 'react';
import { getStytchClient } from '../../../../adapters/react/client/headless-client';
/**
 * Authenticate a member with a recovery (backup) code in place of TOTP/SMS. Works
 * during a forced-login MFA flow (intermediate session token) or with an existing
 * member session. Consuming the code mints a full session.
 */
export function useStytchRecoveryCodesRecover() {
    return useCallback(async (data) => {
        const stytch = getStytchClient();
        if (!stytch) {
            return { success: false, error: 'Stytch client not ready', data: null };
        }
        try {
            const result = await stytch.recoveryCodes.recover({
                organization_id: data.organizationId,
                member_id: data.memberId,
                recovery_code: data.recoveryCode,
                session_duration_minutes: data.sessionDurationMinutes,
            });
            return {
                success: true,
                requestId: result.request_id,
                statusCode: result.status_code,
                memberId: result.member_id,
                sessionJwt: result.session_jwt,
                sessionToken: result.session_token,
                memberSession: result.member_session,
                member: result.member,
                organization: result.organization,
                recoveryCodesRemaining: result.recovery_codes_remaining,
                data: result,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to authenticate with recovery code',
                data: error,
            };
        }
    }, []);
}
/**
 * Rotate (regenerate) the current member's recovery codes. Requires a logged-in
 * member session — `organization_id` and `member_id` are inferred from the session
 * by Stytch. All existing codes are invalidated; the fresh set must be shown once.
 */
export function useStytchRecoveryCodesRotate() {
    return useCallback(async () => {
        const stytch = getStytchClient();
        if (!stytch) {
            return { success: false, error: 'Stytch client not ready', data: null };
        }
        try {
            const result = await stytch.recoveryCodes.rotate();
            return {
                success: true,
                requestId: result.request_id,
                statusCode: result.status_code,
                recoveryCodes: result.recovery_codes,
                data: result,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to regenerate recovery codes',
                data: error,
            };
        }
    }, []);
}
